import { normalizeTags } from './moduleTags.js';
/**
 * Normalize a group name for reuse matching: trim + case-insensitive.
 * Request 0345 — copy groups from a model org.
 */
export function normalizeGroupNameKey(name) {
    return name.trim().toLowerCase();
}
/** Treat null/undefined the same (Firestore update clears optionals as null). */
function parentKey(id) {
    return id ?? undefined;
}
/**
 * Roots first, then children — stable enough for a single-pass copy that maps
 * `parentGroupId` via `sourceId → targetId`.
 */
export function orderGroupsParentFirst(groups) {
    const byId = new Map(groups.map((g) => [g.id, g]));
    const result = [];
    const visiting = new Set();
    const visited = new Set();
    function visit(id) {
        if (visited.has(id) || visiting.has(id))
            return;
        const g = byId.get(id);
        if (!g)
            return;
        visiting.add(id);
        const parent = parentKey(g.parentGroupId);
        if (parent && byId.has(parent))
            visit(parent);
        visiting.delete(id);
        visited.add(id);
        result.push(g);
    }
    for (const g of groups)
        visit(g.id);
    return result;
}
function provisionalTargetId(sourceId) {
    return `__new__:${sourceId}`;
}
export function isProvisionalTargetId(id) {
    return id.startsWith('__new__:');
}
/**
 * Pure plan: reuse-by-name at the same mapped parent, create otherwise; then
 * place target root OMs that match a grouped source OM by `moduleId`.
 */
export function planModuleGroupCopy(sourceGroups, targetGroups, sourceModules, targetModules) {
    const virtual = targetGroups.map((g) => ({
        id: g.id,
        name: g.name,
        parentGroupId: parentKey(g.parentGroupId),
        autoTags: g.autoTags,
    }));
    const actions = [];
    const idMap = new Map();
    for (const source of orderGroupsParentFirst(sourceGroups)) {
        const parentSource = parentKey(source.parentGroupId);
        const parentTargetId = parentSource ? idMap.get(parentSource) : undefined;
        const nameKey = normalizeGroupNameKey(source.name);
        const match = virtual.find((v) => parentKey(v.parentGroupId) === parentKey(parentTargetId) &&
            normalizeGroupNameKey(v.name) === nameKey);
        if (match) {
            const autoTagsUnion = normalizeTags([
                ...(match.autoTags ?? []),
                ...(source.autoTags ?? []),
            ]);
            actions.push({
                kind: 'reuse',
                sourceId: source.id,
                targetId: match.id,
                autoTagsUnion,
            });
            idMap.set(source.id, match.id);
            match.autoTags = autoTagsUnion;
        }
        else {
            const newId = provisionalTargetId(source.id);
            actions.push({
                kind: 'create',
                sourceId: source.id,
                name: source.name,
                ...(source.icon != null ? { icon: source.icon } : {}),
                ...(source.description != null ? { description: source.description } : {}),
                ...(source.order != null ? { order: source.order } : {}),
                ...(parentTargetId != null ? { parentTargetId } : {}),
                ...(source.autoTags != null ? { autoTags: [...source.autoTags] } : {}),
            });
            virtual.push({
                id: newId,
                name: source.name,
                parentGroupId: parentTargetId,
                autoTags: source.autoTags,
            });
            idMap.set(source.id, newId);
        }
    }
    const placements = planRootModulePlacements(idMap, sourceModules, targetModules);
    return { actions, idMap, placements };
}
/**
 * Target OMs at root (no groupId) whose `moduleId` exists in a grouped source OM:
 * place into the mapped group and copy `order`. Never move OMs that already have a group.
 */
export function planRootModulePlacements(idMap, sourceModules, targetModules) {
    const sourceByModuleId = new Map();
    for (const om of sourceModules) {
        const gid = parentKey(om.groupId);
        if (!gid)
            continue;
        if (!idMap.has(gid))
            continue;
        if (!sourceByModuleId.has(om.moduleId)) {
            sourceByModuleId.set(om.moduleId, om);
        }
    }
    const placements = [];
    for (const om of targetModules) {
        if (parentKey(om.groupId))
            continue;
        const sourceOm = sourceByModuleId.get(om.moduleId);
        if (!sourceOm?.groupId)
            continue;
        const mapped = idMap.get(sourceOm.groupId);
        if (!mapped)
            continue;
        placements.push({
            orgModuleId: om.id,
            groupId: mapped,
            ...(sourceOm.order != null ? { order: sourceOm.order } : {}),
        });
    }
    return placements;
}
//# sourceMappingURL=moduleGroupCopy.js.map